import { useAuth } from "modules/auth/Auth"
import { useCallback, useEffect, useRef, useState } from "react"
import { io, Socket } from "socket.io-client"

export type RealtimeConnectionStatus =
	"disconnected" | "connected" | "authenticating"

const REALTIME_EVENTS = [
	"message_status_update",
	"new_message",
	"unread_count_update",
	"voice_call_status_update",
	"ticket_updated",
	"ticket_deleted",
	"escalation_updated",
	"notification_unread_count_update",
] as const

export type RealtimeTransportEvent = (typeof REALTIME_EVENTS)[number]

export const useRealtimeTransport = <TData>({
	serverUrl,
	manual,
	onMessage,
	onError,
	onAuthenticated,
}: {
	serverUrl: string
	manual?: boolean
	onMessage?: (eventName: RealtimeTransportEvent, data: TData) => void
	onError?: (error: Error) => void
	onAuthenticated?: () => void
}) => {
	const { getToken } = useAuth()
	const socketRef = useRef<Socket | null>(null)
	const [connectionStatus, setConnectionStatus] =
		useState<RealtimeConnectionStatus>("disconnected")

	const onMessageRef = useRef(onMessage)
	onMessageRef.current = onMessage

	const onErrorRef = useRef(onError)
	onErrorRef.current = onError

	const onAuthenticatedRef = useRef(onAuthenticated)
	onAuthenticatedRef.current = onAuthenticated

	const getTokenRef = useRef(getToken)
	getTokenRef.current = getToken

	const parsePayload = useCallback((raw: unknown): TData => {
		if (typeof raw === "string") {
			try {
				return JSON.parse(raw) as TData
			} catch (err) {
				console.error("[realtime transport] Failed to parse payload:", err)
				return raw as unknown as TData
			}
		}
		return raw as TData
	}, [])

	const disconnect = useCallback(() => {
		if (socketRef.current) {
			socketRef.current.removeAllListeners()
			socketRef.current.disconnect()
			socketRef.current = null
			setConnectionStatus("disconnected")
		}
	}, [])

	const connect = useCallback(() => {
		if (!socketRef.current) {
			const url = serverUrl.length > 0 ? serverUrl : "/ws"
			console.debug("[realtime transport] Connecting to server:", url)
			const socket = io(url, {
				reconnectionAttempts: 5,
				timeout: 10000,
				withCredentials: true,
				transports: ["websocket"],
			})
			socketRef.current = socket

			const authenticate = async () => {
				try {
					console.debug(
						"[realtime transport] Sending authentication request...",
					)
					const token = await getTokenRef.current()
					socket.emit("authenticate", { token })
				} catch (err) {
					console.error("[realtime transport] Failed to get auth token:", err)
					onErrorRef.current?.(err as Error)
				}
			}

			socket.on("connect", async () => {
				console.debug("[realtime transport] Connected to server")
				setConnectionStatus("authenticating")
				await authenticate()
			})

			socket.on("authenticated", () => {
				console.debug("[realtime transport] Authenticated successfully")
				setConnectionStatus("connected")
				onAuthenticatedRef.current?.()
			})

			socket.on("auth_error", (error) => {
				console.error(
					"[realtime transport] Authentication error:",
					error instanceof Error ? error.message : JSON.stringify(error),
				)
				setConnectionStatus("disconnected")
				onErrorRef.current?.(error)
			})

			socket.on("disconnect", () => {
				console.debug("[realtime transport] Disconnected from server")
				setConnectionStatus("disconnected")
			})

			socket.on("connect_error", (error) => {
				console.error(
					"[realtime transport] Connection error:",
					error instanceof Error ? error.message : JSON.stringify(error),
				)
				setConnectionStatus("disconnected")
				onErrorRef.current?.(error)
			})

			REALTIME_EVENTS.forEach((eventName) => {
				socket.on(eventName, (data: unknown) => {
					console.debug("[realtime transport] Received event:", eventName)
					const parsed = parsePayload(data)
					onMessageRef.current?.(eventName, parsed)
				})
			})
		}
	}, [parsePayload, serverUrl])

	const isConnected = useCallback(() => {
		return socketRef.current?.connected ?? false
	}, [])

	useEffect(() => {
		if (!manual) {
			connect()
		}
		return () => {
			disconnect()
		}
	}, [connect, disconnect, manual])

	return {
		disconnect,
		connect,
		connectionStatus,
		isConnected,
	}
}
